<script setup>
import { ref, watch } from 'vue'
import axios from 'axios'

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close'])

const allNews = ref([])
const loading = ref(false)
const filterStatus = ref('all')
const searchQuery = ref('')

function formatTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

function formatMessageForDisplay(htmlContent) {
  if (!htmlContent) return ''
  
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContent
  
  const links = tempDiv.querySelectorAll('a')
  links.forEach(link => {
    const href = link.getAttribute('href') || link.href
    if (href) {
      try {
        const urlObj = new URL(href)
        link.textContent = urlObj.hostname.replace(/^www\./, '')
      } catch (e) {
        // Keep original text if URL parsing fails
      }
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.style.textDecoration = 'underline'
      link.style.color = '#2563eb'
    }
  })
  
  return tempDiv.innerHTML
}

async function loadAllNews() {
  loading.value = true
  try {
    const params = {}
    if (filterStatus.value !== 'all') {
      params.status = filterStatus.value
    }
    
    const response = await axios.get('/api/players/news/all', { params })
    
    if (response.data?.success && response.data?.news) {
      allNews.value = response.data.news
    }
  } catch (error) {
    console.error('Error loading all news:', error)
    allNews.value = []
  } finally {
    loading.value = false
  }
}

async function deleteNews(newsId) {
  if (!confirm('Are you sure you want to delete this news entry?')) {
    return
  }
  
  try {
    const response = await axios.delete(`/api/players/news/${newsId}`)
    
    if (response.data?.success) {
      // Remove from list
      allNews.value = allNews.value.filter(news => news.id !== newsId)
    }
  } catch (error) {
    console.error('Error deleting news:', error)
    alert('Failed to delete news entry')
  }
}

function getFilteredNews() {
  let filtered = allNews.value
  
  if (filterStatus.value !== 'all') {
    filtered = filtered.filter(news => news.status === filterStatus.value)
  }
  
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(news => 
      news.player_name?.toLowerCase().includes(query) ||
      news.message?.toLowerCase().includes(query) ||
      news.author?.toLowerCase().includes(query)
    )
  }
  
  return filtered
}

function closeModal() {
  emit('close')
}

watch(() => props.show, (newValue) => {
  if (newValue) {
    loadAllNews()
  }
})

watch([filterStatus, searchQuery], () => {
  // Filtering is done in computed property
})
</script>

<template>
  <div v-if="show" 
       class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
       @click.self="closeModal">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">All Player News</h3>
        <button @click="closeModal" 
                class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Filters -->
      <div class="p-4 border-b border-gray-200 bg-gray-50">
        <div class="flex gap-4 items-center">
          <div class="flex-1">
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="Search by player name, message, or author..."
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          </div>
          <div>
            <select 
              v-model="filterStatus"
              @change="loadAllNews"
              class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="questionable">Questionable</option>
              <option value="out">Out</option>
            </select>
          </div>
          <button 
            @click="loadAllNews"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Refresh
          </button>
        </div>
      </div>

      <!-- News List -->
      <div class="flex-1 overflow-y-auto p-4">
        <div v-if="loading" class="text-center text-gray-500 py-8">
          Loading news...
        </div>
        <div v-else-if="getFilteredNews().length === 0" class="text-center text-gray-500 py-8">
          No news found.
        </div>
        <div v-else class="space-y-4">
          <div v-for="news in getFilteredNews()" 
               :key="news.id"
               class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
               :class="{ 'border-l-4 border-l-red-500': !news.read_at }">
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <span class="font-semibold text-gray-900">{{ news.player_name }}</span>
                  <span class="text-xs text-gray-400">(ID: {{ news.player_id }})</span>
                  <span v-if="news.status" 
                        class="text-xs px-2 py-0.5 rounded-full capitalize"
                        :class="{
                          'bg-green-100 text-green-700': news.status === 'active',
                          'bg-orange-100 text-orange-700': news.status === 'questionable',
                          'bg-red-100 text-red-700': news.status === 'out'
                        }">
                    {{ news.status }}
                  </span>
                </div>
                <div class="flex items-center gap-2 mb-2 text-xs text-gray-500">
                  <span>{{ news.author || 'System' }}</span>
                  <span>•</span>
                  <span>{{ formatTime(news.created_at || news.timestamp) }}</span>
                  <span v-if="news.fixture_id">•</span>
                  <span v-if="news.fixture_id">Fixture: {{ news.fixture_id }}</span>
                </div>
                <div class="text-sm text-gray-800 prose prose-sm max-w-none" v-html="formatMessageForDisplay(news.message || news.content)"></div>
              </div>
              <button 
                @click="deleteNews(news.id)"
                class="text-red-500 hover:text-red-700 transition-colors p-2"
                title="Delete news">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.prose a {
  color: #2563eb;
  text-decoration: underline !important;
  text-underline-offset: 2px;
}

.prose a:hover {
  color: #1d4ed8;
  text-decoration: underline !important;
}

.prose img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 4px 0;
}
</style>

